<template>
  <v-container>
    <Loader v-if="loaderEnabled" />

    <!-- Header Container -->
    <v-container :style="{ background: dashboardGradient }">
      <v-row no-gutters align="center">
        <v-col cols="auto">
          <inline-svg
            class="mt-2"
            :src="getNavigationIcon()"
            width="50"
            height="50"
            aria-label="Navigation Icon"
          />
        </v-col>
        <v-col>
          <span
            class="display-1 font-weight-thin white--text pt-2 pl-2"
            v-text="getAccountType()"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pa-2 pb-0">
        <v-spacer />
        <v-col
          v-if="showCurrencySelection"
          cols="8"
          sm="4"
        >
          <v-select
            v-model="currencyType"
            small
            outlined
            dark
            dense
            :items="activeCurrencies"
            :label="$t('productList.currency')"
            @change="listProductsBasesOnCurrency"
            data-qa="productoffers-select-vulxz"
          />
        </v-col>
      </v-row>
    </v-container>
    <!-- Open CASA Account -->
    <OpenCASA
      v-if="enableCASA"
      :accountConfig="casaProductDetails"
      :additionalDetails="model.additionalDetails"
      @cancelDialog="enableCASA = false; closeAccountOperationTypeSelectionModal()"
    />
    <!-- Product Offers skeleton loader -->
    <v-layout class="px-2" wrap v-if="isLoading">
      <v-flex
        v-for="(_, index) in Array.from({ length: 6 })"
        :key="`deposit-skeleton-loader-${index}`"
        xs12
        sm6
        md6
        lg4
        xl4
        class="pa-2"
      >
        <v-skeleton-loader
          elevation="2"
          type="card"
        >
          <v-card elevation="3" class="py-3">
            <v-container fluid class="pb-1">
              <v-row class="mx-2" dense justify="space-between">
                <v-flex xs8 sm8 md8 lg8 xl8>
                  <v-skeleton-loader type="text"></v-skeleton-loader>
                </v-flex>
                <v-flex xs2 sm2 md2 lg2 xl2>
                  <v-skeleton-loader type="avatar"></v-skeleton-loader>
                </v-flex>
              </v-row>
              <v-skeleton-loader :type="`list-item-${i === 0 ? 'three' : 'two'}-line`" v-for="(__, i) in Array.from({ length: 3 })" :key="`line-skel-${i}`"></v-skeleton-loader>
              <br/>
              <v-row class="mx-1 my-2">
              <v-spacer></v-spacer>
              <v-skeleton-loader type="button" class="rounded-pill btn-skel" width="112"></v-skeleton-loader>
              </v-row>
            </v-container>
          </v-card>
        </v-skeleton-loader>
      </v-flex>
    </v-layout>
    <!-- Product Offers skeleton loader -->
    <v-layout class="px-2" wrap data-qa="productoffers-layout-i2ek6">
      <v-flex
        v-for="(product, index) in filterSameCurrencyProducts"
        :key="index"
        xs12
        sm6
        md6
        lg4
        xl4
        class="pa-2"
       :data-qa="`productoffers-flex-7mcvt-${index}`">
        <v-card elevation="3" class="py-3" data-qa="productoffers-card-iw7icv">
          <v-container fluid class="pb-1" data-qa="productoffers-container-t56jw">
            <v-row class="mx-2" dense data-qa="productoffers-row-s94r2a">
              <v-flex xs10 sm10 md10 lg10 xl10 data-qa="productoffers-flex-lnoizc">
                <span class="font-weight-bold subtitle-1"
                   data-qa="productoffers-span-kxt6re">{{ product.label }}
                </span>
              </v-flex>
              <v-flex class="pt-2" xs2 sm1 md1 lg1 xl1 data-qa="productoffers-flex-s0sfg">
                <inline-svg
                  :src="getNavigationIcon()"
                  width="50"
                  height="50"
                  aria-label="Navigation Icon"
                ></inline-svg>
              </v-flex>
            </v-row>
            <!-- TD Products -->
            <v-flex
              v-if="config.accountType === 'TD'"
              class="mx-2 my-2"
              xs12
              sm12
              md12
              lg12
              xl12
              data-qa="productoffers-flex-09h8hc">
              <v-flex class="pt-4" data-qa="productoffers-flex-0v5d43">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                   data-qa="productoffers-span-klksha">{{ $t("productList.minMaxAmountLabel") }}</span
                >
                <br data-qa="productoffers-br-log9tm"  />
                {{
                  $formatter.currency.format(
                    product.minAmount[0].value,
                    product.minAmount[0].currency,
                    isDecimalRequiredForMinMaxAmount
                  )
                }}
                -
                {{
                  $formatter.currency.format(
                    product.maxAmount[0].value,
                    product.maxAmount[0].currency,
                    isDecimalRequiredForMinMaxAmount
                  )
                }}
              </v-flex>
              <v-flex class="pt-4" data-qa="productoffers-flex-9lef1">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                   data-qa="productoffers-span-bq2g">{{ $t("productList.interestType") }}</span
                >
                <br data-qa="productoffers-br-toxqw"  />
                {{
                  product.interestRateFloating === "No"
                    ? $t("productList.rateOfInterestFixed")
                    : $t("productList.interestRateFloating")
                }}
              </v-flex>
              <v-flex class="pt-4" data-qa="productoffers-flex-p2tq62">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                   data-qa="productoffers-span-7302lr">{{ $t("productList.minMaxTermLabel") }}</span
                >
                <br data-qa="productoffers-br-qc19wb"  />
                {{ product.minTenor[0].value }} -
                {{ product.maxTenor[0].value }}
                {{ getROIfrequency(product.minTenor[0].frequency) }}
              </v-flex>
              <v-flex class="pt-4" data-qa="productoffers-flex-29eaof">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                   data-qa="productoffers-span-iv1lbj">{{ $t("productList.currency") }}</span
                >
                <br data-qa="productoffers-br-w0erco"  />
                {{ product.maxAmount[0].currency }}
              </v-flex>
              <v-flex class="pt-4" data-qa="productoffers-flex-zbqbn">
                <a
                  text
                  class="font-weight-light"
                  :style="{ color: primaryColor }"
                  data-qa="showInterestRates"
                  @click="showInterestRates(product, index)"
                  >{{ $t("productList.viewInterestRateLabel") }}
                </a>
              </v-flex>
            </v-flex>
            <!-- CASA Products -->
            <v-flex
              v-if="config.accountType !== 'TD'"
              class="mx-2 my-2"
              xs12
              sm12
              md12
              lg12
              xl12
             data-qa="productoffers-flex-80cl06">
              <v-flex class="pt-4" data-qa="productoffers-flex-tv96kg">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                   data-qa="productoffers-span-zqfh9s">{{ $t("productList.minAccOpeningBalLabel") }}</span
                >
                <br data-qa="productoffers-br-ufae6"  />
                {{
                  $formatter.currency.format(product.minBal, product.currency, isDecimalRequiredForMinMaxAmount)
                }}
              </v-flex>
              <!-- Checking for CASA interest rates - Start-->
               <!-- Customization Disable the Interest Details-->
              <v-flex v-if="enableAccountsInterestDetailsEnquiry" class="pt-4" data-qa="productoffers-flex-j7mwjl">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                 data-qa="productoffers-span-ch2wsp">
                  {{ $t("productList.creditInterestRateFixed") }}
                </span>
                <br data-qa="productoffers-br-dwbozh"  />
                <!-- Variable interest rates -->
                <span v-if="isInterestRatesFixed(product.productSummary, 'creditInterestBaseCode')" data-qa="productoffers-span-5jyacc">
                  {{ $t("productList.interestRateFloating") }}
                  <br data-qa="productoffers-br-e0yywi"  />
                  <a
                    text
                    class="font-weight-light"
                    :style="{ color: primaryColor }"
                    data-qa="showInterestRates"
                    @click="showCASAInterestRates(product, 'creditInterestBaseCode')"
                    >{{ $t("productList.viewInterestRateLabel") }}
                  </a>
                </span>
                <!-- Fixed interest rates -->
                <span v-else data-qa="productoffers-span-rlo9jp">
                  {{ product.creditInterestRateFixed }}% - {{ $t("productList.rateOfInterestFixed") }}
                </span>
              </v-flex>
              <!-- Customization -- Disable the Interest Details -->
              <v-flex v-if="config.accountType !== 'SVGS' && enableAccountsInterestDetailsEnquiry" class="pt-4" data-qa="productoffers-flex-w383p">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                  data-qa="productoffers-span-4bitw">
                  {{ $t("productList.debitInterestRateFixed") }}
                </span>
                <br data-qa="productoffers-br-3kw3di"  />
                <!-- Variable interest rates -->
                <span v-if="isInterestRatesFixed(product.productSummary, 'debitInterestBaseCode')" data-qa="productoffers-span-fb4hw">
                  {{ $t("productList.interestRateFloating") }}
                  <br data-qa="productoffers-br-8rrsfq"  />
                  <a
                    text
                    class="font-weight-light"
                    :style="{ color: primaryColor }"
                    data-qa="showInterestRates"
                    @click="showCASAInterestRates(product, 'debitInterestBaseCode')"
                    >{{ $t("productList.viewInterestRateLabel") }}
                  </a>
                </span>
                <!-- Fixed interest rates -->
                <span v-else data-qa="productoffers-span-c1qhf">
                  {{ product.debitInterestRateFixed }}% - {{ $t("productList.rateOfInterestFixed") }}
                </span>
              </v-flex>
              <!-- Checking for CASA interest rates - End-->
              <v-flex class="pt-4" data-qa="productoffers-flex-eb6kz">
                <span
                  class="pt-4 subtitle-2 font-weight-bold text-uppercase overline"
                  data-qa="productoffers-span-4v8bq9">{{ $t("productList.currency") }}</span
                >
                <br data-qa="productoffers-br-g2jrl"  />
                {{ product.currency }}
              </v-flex>
            </v-flex>
          </v-container>
          <!-- Actions -->
          <v-row dense data-qa="productoffers-row-orh0u">
            <v-spacer data-qa="productoffers-spacer-atqy4o"  />
            <v-btn
              class="text-capitalize font-weight-bold mx-3"
              :color="primaryColor"
              @click="verifyDetails(product, config.accountType)"
              text
              data-qa="productoffers-btn-0k1fep">
              {{ $t("homeCards.openNow") }}
            </v-btn>
          </v-row>
        </v-card>
      </v-flex>
    </v-layout>
    <!-- Products Not Avaialable Error -->
    <v-flex v-if="isProductNotAvailable" xs10 sm10 md10 lg10 xl8 class="text-left ml-2 py-4" data-qa="productoffers-flex-7s7pgo">
      <v-alert icon="mdi-information-outline" class="v-alert--info" data-qa="productoffers-alert-clifto">
        <font class="body-2">
          {{ $t("productList.noOffersAvailable") }}
        </font>
      </v-alert>
    </v-flex>
     <!-- CASA View Interest Rates Modal -->
    <Modal :config="casaInterestModalConfig" @cancel="casaInterestModalConfig.enabled = false">
      <span slot="modalContent" data-qa="productoffers-span-gd0wbc">
        <!-- CASA Interest Rates Vary Note -->
        <v-flex xs12 sm8 md8 lg8 xl8 class="text-left" data-qa="productoffers-flex-o5zk3p">
          <v-alert icon="mdi-information-outline" class="v-alert--info" data-qa="productoffers-alert-n3twck">
            <font class="body-2">
              {{ $t("productList.casaInterestRatesVaryModalMsg") }}
            </font>
          </v-alert>
        </v-flex>
        <v-data-table
          :headers="casaInterestModalHeaders"
          :items="casaInterestRates"
          hide-default-footer
          multi-sort
          class="elevation-1"
         data-qa="productoffers-data-table-bf0f6p"></v-data-table>
      </span>
    </Modal>

    <!-- TD View Interest Rates Modal -->
    <Modal :config="modalConfig" @cancel="hideInterests">
      <span slot="modalContent" data-qa="productoffers-span-19yncc">
        <v-data-table
          :headers="headers"
          :items="interestRates"
          multi-sort
          class="elevation-1"
         data-qa="productoffers-data-table-0weor9"></v-data-table>
      </span>
    </Modal>

    <!-- Account Operation Type Selection Modal -->
    <!-- TODO: Move to a separate component -->
    <Modal
      :config="accountOperationTypeSelectionModal"
      @cancel="closeAccountOperationTypeSelectionModal"
      @btn2Action="closeAccountOperationTypeSelectionModal"
      @btn1Action="onConfirmAccountOperationType"
    >
      <template slot="modalContent">
        <span class="font-weight-medium text-subtitle-1">{{ $t("openDepositAcc.accountTypeSelection") }}</span>
        <v-radio-group v-model="selectedAccountOperationType" row @change="checkSaveButtonStatus">
          <v-radio
          v-for="(item, index) in availableOperationTypes"
          :key="`modal-radio-item-${index}`"
          :value="item.value"
          :color="primaryColor"
          >
            <template v-slot:label>
            <span :class="[selectedAccountOperationType === item.value ? 'font-weight-bold text-body-1' : 'font-weight-light text-body-1', 'black--text']">{{ item.text }}</span>
            </template>
          </v-radio>
        </v-radio-group>
        <v-expand-transition>
          <div v-if="selectedAccountOperationType === 'JOINT'">
          <v-alert icon="mdi-information-outline" class="v-alert--info" style="border-style: dashed;" v-if="!hasJointAccounts" rounded="xl">
            <h4 class="font-weight-bold text-subtitle-1 mb-1">{{ $t("openDepositAcc.noJointAccount") }}</h4>
            <p class="font-weight-medium text-subtitle-1 grey--text" style="line-height: 24px;">{{ $t("openDepositAcc.errorMessageNoJointAccount") }}</p>
          </v-alert>
          <template v-else>
            <span class="font-weight-medium text-subtitle-1 black--text">{{ $t("openDepositAcc.jointAccDetails") }}</span>
            <v-row align="center" wrap class="pt-1">
              <v-col
                class="d-flex"
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="selectedJointAccountHolder"
                  :label= "$t('openDepositAcc.jointAccOwners')"
                  :items="jointOwnerList"
                  filled
                  max-height="auto"
                  :color="primaryColor"
                  return-object
                  item-value="jointCustomerId"
                  :item-text="(item)=>`${item.jointCustomerName} - ${item.jointCustomerId}`"
                  class="product-offers-custom-textfield"
                  @change="checkSaveButtonStatus"
                ></v-select>
              </v-col>
              <v-col
                class="d-flex"
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="selectedModeOfOperation"
                  :label="$t('openDepositAcc.modeOfOperation')"
                  :items="modeOfOperations"
                  filled
                  max-height="auto"
                  :color="primaryColor"
                  item-value="value"
                  item-text="text"
                  class="product-offers-custom-textfield"
                  @change="checkSaveButtonStatus"
                ></v-select>
              </v-col>
            </v-row>
          </template>
        </div>
        </v-expand-transition>
      </template>
    </Modal>
    <Modal
      :config="lisaAccOpenConfirmation"
      @btn1Action="confirmLisaOpening"
    />
    <Modal
      :config="accountOpeningErrorModalConfig"
      @btn2Action="$router.push('/home')"
      @btn1Action="retryAction"
    />
    <VerifyPersonalDetails
      v-if="showModal.verifyPersonalDetails"
      @cancel="showModal.verifyPersonalDetails=false"
      @continue="openOfferUdfForm"
    />
    <OfferUdfForm
      v-model="model.additionalDetails"
      :show="showModal.additionalDetails"
      @cancel="closeAdditionalDetailsModal"
      @continue="openReviewTermsAndConditions"
    />
    <ReviewConsentDetails
      v-if="showModal.reviewConsent"
      :consentConfig="consentConfig"
      @cancel="showModal.reviewConsent=false"
      @continue="onConfirmAndContinue"
    />
  </v-container>
</template>

<script>
import OpenCASA from '@/views/accounts/openSavingsAccount/OpenSavingsAccount.vue'
import commonService from '@/service/commonService.js'
import preload from '@/util/preload'
import store from '@/core/store'
import _ from 'lodash'
import Loader from '../../components/common/Loader.vue'
import OfferUdfForm from './OfferUdfForm.vue'

export default {
  name: 'ProductOffers',
  components: {
    Loader,
    OpenCASA,
    Modal: () => import('@/components/common/Modal.vue'),
    VerifyPersonalDetails: () => import('@/views/accounts/consent/VerifyPersonalDetails.vue'),
    ReviewConsentDetails: () => import('@/views/accounts/consent/ReviewConsentDetails.vue'),
    OfferUdfForm
  },
  props: {
    config: {
      type: Object
    }
  },
  data () {
    return {
      enableCASA: false,
      casaProductDetails: {},
      activeCurrencies: [],
      productList: [],
      tdProductList: [],
      currentProductList: [],
      savingsProductList: [],
      products: [],
      accountType: '',
      currencyType: '',
      modalConfig: {
        enabled: false,
        persistent: false,
        close: true,
        outlinedSaveBtn: true,
        btn1Label: this.$t('common.okay'),
        title: this.$t('productList.modalTitle'),
        width: 800
      },
      headers: [
        { text: this.$t('productList.amountModalLable'), value: 'minMaxAmount' },
        { text: this.$t('productList.termModalLable'), value: 'minMaxTenor' },
        { text: this.$t('productList.interestRateModalLable'), value: 'effectiveIR' }
      ],
      casaInterestModalConfig: {
        enabled: false,
        persistent: false,
        close: true,
        outlinedSaveBtn: true,
        btn1Label: this.$t('common.okay'),
        title: this.$t('productList.modalTitle'),
        width: 700
      },
      casaInterestModalHeaders: [
        { text: this.$t('productList.accountBalanceModalLable'), value: 'accountBal' },
        { text: this.$t('productList.interestRateModalLable'), value: 'effectiveIR' }
      ],
      interestRates: [],
      casaInterestRates: [],
      loaderEnabled: false,
      isProductNotAvailable: false,
      showCurrencySelection: this.$appSettings.productOffer.enableCurrencySelection,
      accountOperationTypeSelectionModal: {
        enabled: false,
        persistent: false,
        close: true,
        btn2Label: this.$t('common.cancelButton_text'),
        btn1Label: this.$t('openDepositAcc.confirmContinue'),
        outlinedCancelBtn: true,
        width: 750
      },
      selectedAccountOperationType: 'SOLE',
      availableOperationTypes: [{
        value: 'SOLE',
        text: this.$t('openDepositAcc.soleAccount')
      }, {
        value: 'JOINT',
        text: this.$t('openDepositAcc.jointAccount')
      }],
      modeOfOperations: [{
        value: 'JOINT',
        text: this.$t('systemEnums.modeOfOperations.JOINT')
      }, {
        value: this.config.accountType === 'SVGS' ? 'EITHERORSURVIVOR' : 'EITHER-OR-SURVIVOR',
        text: this.$t('systemEnums.modeOfOperations.EITHERORSURVIVOR')
      }, {
        value: 'ANYONE-OR-SURVIVOR',
        text: this.$t('systemEnums.modeOfOperations.ANYONE-OR-SURVIVOR')
      }],
      jointOwnerList: [],
      selectedJointAccountHolder: '',
      selectedModeOfOperation: '',
      isLoading: false,
      allowedAccountTypesForJointAccountOpening: this.$appSettings.defaultConfigurations.allowedAccountTypesForJointAccountOpening,
      isDecimalRequiredForMinMaxAmount: this.$appSettings.defaultConfigurations.showDecimalPartInOpenAcountMinMaxAmount,
      lisaAccOpenConfirmation: {
        enabled: false,
        persistent: true,
        close: true,
        btn1Label: this.$t('openSavingsAcc.btn.confirm'),
        btn2Label: this.$t('openSavingsAcc.btn.cancel'),
        body: this.$t('openSavingsAcc.lisaOpenWarning'),
        outlinedCancelBtn: true,
        actionOrder: 'cancel-save',
        width: 600
      },
      accountOpeningErrorModalConfig: {
        enabled: false,
        persistent: true,
        btn1Label: this.$t('openSavingsAcc.tryAgain'),
        btn2Label: this.$t('openSavingsAcc.btn.toHome'),
        title: this.$t('openSavingsAcc.errorMsg'),
        body: this.$t('termsAndConditions.error.serviceError'),
        outlinedCancelBtn: true,
        actionOrder: 'cancel-save',
        width: 600
      },
      enableVerifyPersonalDetails: this.$appSettings.defaultConfigurations.consentManagement.enableVerifyPersonalDetails,
      enableTCForAccountOpening: this.$appSettings.defaultConfigurations.consentManagement.enableTCForAccountOpening,
      enableAccountsInterestDetailsEnquiry: this.$appSettings.defaultConfigurations.enableAccountsInterestDetailsEnquiry,
      customerDetails: {},
      addressDetails: {},
      acceptConsentCheckbox: false,
      productSelected: {},
      acceptedConsents: [],
      consentConfig: [],
      customerCode: JSON.parse(sessionStorage.getItem('customerCode')),
      feusId: JSON.parse(sessionStorage.getItem('username')),
      subEntity1: '',
      model: {
        additionalDetails: null
      },
      showModal: {
        verifyPersonalDetails: false,
        additionalDetails: false,
        reviewConsent: false
      }
    }
  },
  async created () {
    await this.getActiveCurrencyFromService()
    const { customerDetails, originalResidentialAddr } = store.getters['profileStore/profileData']
    this.customerDetails = customerDetails
    this.addressDetails = originalResidentialAddr
  },
  computed: {
    isSmeLogin () {
      return JSON.parse(sessionStorage.getItem('isSmeLogin'))
    },
    hasJointAccounts () {
      return this.jointOwnerList.length > 0
    },
    filterSameCurrencyProducts () {
      if (this.products && this.products.length) {
        const sameCurrencyProducts = this.products.filter((item) => {
          if (this.config.accountType === 'TD') {
            return item.maxAmount[0].currency === this.currencyType
          } else {
            return item.currency === this.currencyType
          }
        })
        return sameCurrencyProducts
      } else return []
    },
    shouldShowAdditionalDetails () {
      if (this.isSmeLogin) return false

      const { enabled, productTypeExclusionList: excludedList } =
        this.$appSettings.defaultConfigurations.openAccountUdf
      if (!enabled) return false

      const { accountType } = this.config
      const productType = ['TD', 'CACC'].includes(accountType)
        ? accountType
        : this.productSelected.productSummary.productType

      return !excludedList.includes(productType)
    }
  },
  methods: {
    onConfirmAccountOperationType () {
      if (this.selectedAccountOperationType === 'SOLE') return this.openAccount(...this.accountOperationTypeSelectionModal.storedData)
      const accountOperationTypeDetails = {
        selectedAccountOperationType: this.selectedAccountOperationType,
        selectedJointAccountHolder: this.selectedJointAccountHolder,
        selectedModeOfOperation: this.selectedModeOfOperation
      }
      return this.openAccount(...this.accountOperationTypeSelectionModal.storedData, accountOperationTypeDetails)
    },
    checkSaveButtonStatus () {
      if (this.selectedAccountOperationType === 'SOLE') {
        this.accountOperationTypeSelectionModal.enableCancel = false
        this.selectedJointAccountHolder = ''
        this.selectedModeOfOperation = ''
        return
      }
      if (this.selectedJointAccountHolder && this.selectedModeOfOperation) {
        this.accountOperationTypeSelectionModal.enableCancel = false
        return
      }
      this.accountOperationTypeSelectionModal.enableCancel = true
    },
    closeAccountOperationTypeSelectionModal () {
      delete this.accountOperationTypeSelectionModal.storedData
      this.accountOperationTypeSelectionModal.enabled = false
      this.accountOperationTypeSelectionModal.enableCancel = false
      this.selectedAccountOperationType = 'SOLE'
      this.selectedJointAccountHolder = ''
      this.selectedModeOfOperation = ''
    },
    checkJointAccountOpeningPossibility (product, offerType) {
      const allowedOperationTypes = ['JOINT', 'BOTH']
      const { productSummary: { applicableAccountOperationType = undefined, productDescription = undefined, productType = '' } = {} } = product
      const accountType = offerType === 'SVGS' ? productType : offerType
      // if account type != TD or SVGS, Or
      // if applicableAccountOperationType is not JOINT or BOTH then the joint account opening feature is out of scope - so proceed with the usual flow
      if (this.isSmeLogin || !this.allowedAccountTypesForJointAccountOpening.includes(accountType) || !allowedOperationTypes.includes(applicableAccountOperationType)) return this.openAccount(product, offerType)
      // fetch joint owner list from user store
      this.jointOwnerList = Object.values(store.getters['profileStore/profileData']?.jointHolderDetails ?? {})
      // show modal for user to select the oeration type
      this.accountOperationTypeSelectionModal.title = `Open New ${productDescription || 'Account'}`
      this.accountOperationTypeSelectionModal.storedData = [product, offerType]
      this.accountOperationTypeSelectionModal.enabled = true
    },
    async getActiveCurrencyFromService () {
      self.loaderEnabled = true
      const cachedCurrencies = store.getters['profileStore/profileData'].cachedCurrencies
      if (cachedCurrencies && cachedCurrencies.length) {
        this.activeCurrencies = cachedCurrencies
        this.currencyType = this.$appSettings.defaultConfigurations.currencyFormat.currency
        const isCalled = this.isProductsCached()
        if (!isCalled) {
          await this.getListOfProducts()
        } else {
          this.productList = store.getters['profileStore/profileData'].allProducts
          this.filterProducts()
        }
      } else {
        this.currencyType = this.$appSettings.defaultConfigurations.currencyFormat.currency
        this.activeCurrencies.push(this.currencyType)
        const resp = await commonService.getActiveCurrencyFromService()
        if (resp && resp.items) {
          for (const item of resp.items) {
            if (item !== this.currencyType) {
              this.activeCurrencies.push(item)
            }
          }
          store.dispatch('profileStore/setProfileData', {
            cachedCurrencies: this.activeCurrencies
          })
          const isCalled = this.isProductsCached()
          if (!isCalled) {
            await this.getListOfProducts()
          }
        }
      }
      self.loaderEnabled = false
    },
    isProductsCached () {
      const profileStore = store.getters['profileStore/profileData']
      let isCalled = false
      switch (this.config.accountType) {
        case 'TXLA':
        case 'NACC':
        case 'TXNA':
        case 'SVGS':
        case 'CACC':
          if (profileStore.casaProducts) {
            const keys = Object.keys(profileStore.casaProducts)
            isCalled = keys.includes(this.currencyType)
          }
          break
        case 'TD':
          if (profileStore.tdProducts) {
            const keys = Object.keys(profileStore.tdProducts)
            isCalled = keys.includes(this.currencyType)
          }
          break
        default:
          break
      }
      return isCalled
    },
    selectedCurrencyProducts () {
      this.isProductNotAvailable = false
      if (this.currencyType) {
        const temp = _.filter(this.products, product => {
          return this.config.accountType === 'TD' ? product.minAmount[0].currency === this.currencyType : product.currency === this.currencyType
        })
        if (!temp.length) {
          this.isProductNotAvailable = true
        }
      }
    },
    async listProductsBasesOnCurrency () {
      const isCalled = this.isProductsCached()
      if (!isCalled) {
        await this.getListOfProducts()
      }
      this.selectedCurrencyProducts()
    },
    async getListOfProducts () {
      this.isLoading = true
      this.productList = await preload.getListOfProducts(this.config.accountType, this.currencyType)
      this.filterProducts()
      this.isLoading = false
    },
    getAccountType () {
      let label
      switch (this.config.accountType) {
        case 'TXLA':
        case 'NACC':
        case 'TXNA':
        case 'SVGS':
          label = this.$t('productList.savingsAccountHeaderLable')
          break
        case 'CACC':
          label = this.$t('productList.currentAccountHeaderLable')
          break
        default:
          label = this.$t('productList.depositAccountHeaderLable')
          break
      }
      return label
    },
    getROIfrequency (frequency) {
      return this.$t(`productList.units.${frequency}`)
    },
    getInterestPayoutMethod (interestPayoutMethod, payoutFrequencyPeriod) {
      return interestPayoutMethod === 'PIP'
        ? this.$t(`productList.interestPayoutMethods.${interestPayoutMethod}`) +
            ', ' +
            this.$t(`productList.units.${payoutFrequencyPeriod}`)
        : this.$t(`productList.interestPayoutMethods.${interestPayoutMethod}`)
    },
    getNavigationIcon () {
      let resultIcon
      switch (this.config.accountType) {
        case 'TXLA':
        case 'NACC':
        case 'TXNA':
        case 'SVGS':
          resultIcon = this.getSvgIconSrc('Savings2')
          break
        case 'CACC':
          resultIcon = this.getSvgIconSrc('Current2')
          break
        case 'TD':
          resultIcon = this.getSvgIconSrc('Deposit2')
          break
        default:
          break
      }
      return resultIcon
    },
    showInterestRates (offer, index) {
      this.headers[0].text = `${this.$t('productList.amountModalLable')} (${offer.minAmount ? offer.minAmount[0].currency : ''})`
      this.modalConfig.enabled = true
      this.interestRates = []
      if (offer.interestRates) {
        this.interestRates = offer.interestRates
      } else {
        this.getTermDepositInterestRates(offer, index)
      }
    },
    showCASAInterestRates (offer, rateType) {
      this.casaInterestModalConfig.enabled = true
      this.casaInterestRates = rateType === 'creditInterestBaseCode' ? offer.creditInterestRates : offer.debitInterestRates
      if (!this.casaInterestRates) {
        this.getCASAInterestRates(offer, rateType)
      }
    },
    hideInterests () {
      this.modalConfig.enabled = false
    },
    async getCASAInterestRates (offer, rateType) {
      const self = this
      const allProducts = store.getters['profileStore/profileData'].allProducts
      if (offer.productSummary) {
        const res = await commonService.fetchCASAProductsInterestRates(offer.productSummary[rateType])
        if (res && res.baseCodeIntRatesResponse) {
          const { tieredInterestRate } = res.baseCodeIntRatesResponse
          self.casaInterestRates = []
          if (tieredInterestRate && tieredInterestRate.length) {
            for (const rate of tieredInterestRate) {
              self.casaInterestRates.push({
                accountBal: this.$formatter.currency.format(
                  rate.balanceTo.value,
                  rate.balanceTo.currency
                ),
                effectiveIR: `${rate.interestRate} %`
              })
            }
          } else {
            self.casaInterestRates = [
              {
                accountBal: 'NA',
                effectiveIR: rateType === 'creditInterestBaseCode' ? `${offer.productSummary.creditInterestRateAttachedToBaseCode} %`
                  : `${offer.productSummary.debitInterestRateAttachedToBaseCode} %`
              }
            ]
          }
        }
      }
      rateType === 'creditInterestBaseCode' ? offer.creditInterestRates = self.casaInterestRates : offer.debitInterestRates = self.casaInterestRates
      switch (this.config.accountType) {
        case 'TXLA':
        case 'NACC':
        case 'TXNA':
        case 'SVGS':
          allProducts.savings.savingsProducts = this.products
          break
        case 'CACC':
          allProducts.current.currentProducts = this.products
          break
        default:
          break
      }
      store.dispatch('profileStore/setProfileData', {
        allProducts: allProducts
      })
    },
    isInterestRatesFixed (productSummary, rateType) {
      return Boolean(productSummary[rateType])
    },
    async getTermDepositInterestRates (offer, index) {
      const self = this
      const interestRate = store.getters['userStore/userData'].tdProducts[index]
        ? store.getters['userStore/userData'].tdProducts[index].interestRates : false
      if (!interestRate) {
        await commonService
          .getTermDepositInterestRates(offer.interestRateId)
          .then((res) => {
            for (const i in res.interestRateTiers) {
              const calculatedInterests = {
                ...res.interestRateTiers[i],
                minMaxAmount: `${self.$formatter.currency.format(
                  res.interestRateTiers[i].balanceFrom, offer.minAmount[0].currency
                )} - ${self.$formatter.currency.format(
                  res.interestRateTiers[i].balanceTo, offer.minAmount[0].currency
                )}`,
                minMaxTenor: `${
                  res.interestRateTiers[i].tenorFrom + ' ' + res.duration
                } - ${res.interestRateTiers[i].tenorTo + ' ' + res.duration}`,
                minAmount: self.$formatter.currency.format(
                  res.interestRateTiers[i].balanceFrom, offer.minAmount[0].currency
                ),
                maxAmount: self.$formatter.currency.format(
                  res.interestRateTiers[i].balanceTo, offer.minAmount[0].currency
                ),
                minTenor: res.interestRateTiers[i].tenorFrom + ' ' + res.duration,
                maxTenor: res.interestRateTiers[i].tenorTo + ' ' + res.duration,
                effectiveIR: res.interestRateTiers[i].interestRate + '%'
              }
              self.interestRates.push(calculatedInterests)
            }
            offer.interestRates = self.interestRates
            store.dispatch('userStore/setUserData', {
              tdProducts: self.products
            })
          })
      } else {
        self.interestRates =
          store.getters['userStore/userData'].tdProducts[index].interestRates
        offer.interestRates = self.interestRates
      }
    },
    filterProducts () {
      this.currentProductList = [...this.productList.current.currentProducts]
      this.savingsProductList = [...this.productList.savings.savingsProducts]
      this.tdProductList = [...this.productList.deposit.tdProducts]
      if (!store.getters['userStore/userData'].tdProducts) {
        store.dispatch('userStore/setUserData', {
          tdProducts: this.tdProductList
        })
      }
      switch (this.config.accountType) {
        case 'TXLA':
        case 'NACC':
        case 'TXNA':
        case 'SVGS':
          this.products = this.savingsProductList
          break
        case 'CACC':
          this.products = this.currentProductList
          break
        default:
          this.products = this.tdProductList
      }
      this.loaderEnabled = false
      this.selectedCurrencyProducts()
    },
    openAccount (product, offerType, accountOperationTypeDetails = {}) {
      if (['SVGS', 'CACC'].includes(offerType) || ['TXNA', 'NACC', 'TXLA'].includes(product.productSummary.productType)) {
        this.enableCASA = true
        this.casaProductDetails = { offerInfo: product, type: offerType }
        // check for casa joint account opening enabled products
        if (this.allowedAccountTypesForJointAccountOpening.includes(product.productSummary.productType)) {
          this.casaProductDetails.accountOperationTypeDetails =
            !('selectedAccountOperationType' in accountOperationTypeDetails)
              ? { selectedAccountOperationType: 'SOLE' }
              : accountOperationTypeDetails
        }
        this.casaProductDetails.consent = this.getAcceptedConsents
      } else {
        this.$router.push({
          name: 'OpenAccount',
          params: { item: product, type: offerType, accountOperationTypeDetails, consent: this.getAcceptedConsents, additionalDetails: this.model.additionalDetails }
        })
      }
    },
    verifyDetails (product, offerType) {
      this.model.additionalDetails = null
      const isSmeLogin = JSON.parse(sessionStorage.getItem('isSmeLogin'))
      if (isSmeLogin || offerType === 'CACC') return this.checkJointAccountOpeningPossibility(product, offerType)
      this.productSelected = product
      this.accountType = offerType
      const accountType = offerType === 'SVGS' ? product?.productSummary?.productType : offerType
      this.subEntity1 = this.getAccountTypeValue(accountType)
      if (product?.productSummary?.productType === 'TXLA') {
        this.lisaAccOpenConfirmation.title = this.$t('openSavingsAcc.askForAccOpen') + ' ' + product.productSummary.productDescription
        this.lisaAccOpenConfirmation.enabled = true
      } else {
        this.checkConfigForDetailsVefification()
      }
    },
    getAccountTypeValue (accountType) {
      const accountTypeMap = {
        TXSV: 'TAXSAVINGS',
        NACC: 'NOTICE',
        TXNA: 'TAXSAVINGSNOTICE',
        TXFR: 'TAXSAVINGSNOTICE',
        TXEA: 'TAXSAVINGSNOTICE',
        CACC: 'CURRENTACCOUNT',
        TXLA: 'TAXSAVINGS',
        SVGS: 'SAVINGS',
        TD: 'FIXDEP'
      }
      return accountTypeMap[accountType] || 'SAVINGS'
    },
    checkConfigForDetailsVefification () {
      if (this.enableVerifyPersonalDetails) {
        this.showModal.verifyPersonalDetails = true
      } else {
        this.openReviewTermsAndConditions()
      }
    },
    confirmLisaOpening () {
      this.lisaAccOpenConfirmation.enabled = false
      this.checkConfigForDetailsVefification()
    },
    openOfferUdfForm () {
      this.showModal.verifyPersonalDetails = false
      if (this.shouldShowAdditionalDetails) {
        this.showModal.additionalDetails = true
      } else {
        this.openReviewTermsAndConditions()
      }
    },
    async openReviewTermsAndConditions () {
      this.showModal.verifyPersonalDetails = false
      this.showModal.additionalDetails = false
      if (this.enableTCForAccountOpening) {
        await this.getAccountOpeningConsents()
        if (this.consentConfig.length > 0) {
          this.showModal.reviewConsent = true
        } else {
          this.checkJointAccountOpening()
        }
      } else {
        this.checkJointAccountOpening()
      }
    },
    async getAccountOpeningConsents () {
      const entityInfo = {
        entityType: 'ACCOUNT',
        subEntity1: this.subEntity1,
        subEntity2: this.productSelected?.offerId ?? ''
      }
      this.consentConfig = await commonService.assignedEntityTCTypes(entityInfo)
      if (this.isServiceUnavailable(this.consentConfig)) {
        this.accountOpeningErrorModalConfig.enabled = true
        return
      }

      await Promise.all(
        this.consentConfig.map(async (item) => {
          const consentInfo = await commonService.getActiveConsentInfo(item.tcTypeCode)
          item.contents = consentInfo.contents
        })
      )
    },
    isServiceUnavailable (response) {
      const status = response?.data?.status || response?.status
      const serviceNotAvailableStatusList = [401, 500]
      return serviceNotAvailableStatusList.includes(status)
    },
    retryAction () {
      this.accountOpeningErrorModalConfig.enabled = false
      this.verifyInfoModalConfig.enabled = false
    },
    onConfirmAndContinue (consent) {
      this.checkJointAccountOpening()
    },
    checkJointAccountOpening () {
      this.showModal.reviewConsent = false
      this.getAcceptedConsents = this.consentConfig.filter(consent => consent.tcAccepted === true).map(consent => ({
        consentCode: consent.tcTypeCode,
        consentVersion: consent.version
      }))
      this.checkJointAccountOpeningPossibility(this.productSelected, this.config.accountType)
    },
    closeAdditionalDetailsModal () {
      this.showModal.additionalDetails = false
      this.model.additionalDetails = null
    }
  }
}
</script>

<style lang="scss">
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-size: 15px;
}

.product-offers-custom-textfield > .v-input__control > .v-input__slot:before {
  border-style: solid;
  border-width: 0.5px;
  border-color: $defaultBorderColor !important;
}

.product-offers-custom-textfield > .v-input__control > .v-input__slot {
  background-color: #f0eff7 !important;
}

.btn-skel > .v-skeleton-loader__button {
  width: 100% !important;
}
</style>
